import React, { useEffect, useState } from 'react';
import PageLayout from '../layouts/PageLayout';
import '../styles/pages/index.css';
import ImageCard from 'components/ImageCard';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import { getAboutPageData } from '../api-sanity/api';
import ThinImagesSection from 'containers/ThinImagesSection';
import HomeFPCarousel from 'containers/HomeFPCarousel';
import NewsIcons from 'components/NewsIcons';
import HeroSection from 'components/HeroSection';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import RollingMenu from 'components/RollingMenu';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '761e876b-544f-4737-a44b-aeeb8890947f',
  P_2: '2856e466-07b7-4b4d-be34-b2593af532ec',
  P_3: 'df61dcc8-0193-4981-93b2-558bd9294df3',
  P_4: '6d8f0316-255d-4c64-a2b2-364b0a269abd',
};

const aboutIds = {
  heroId: '27c45ac5-941c-4d17-97a6-94424c5e64de',
  rollingMenuId: '7d57e7d9-9429-4859-aabb-6e899a030508',
  brochureId: 'c7ea00dc-d35a-4e53-bd7a-bf9e529cf369',
  newsIconsId: 'fe393a21-669a-48f6-a69d-245c93fd54f3',
  homeSliderId: '10e45daf-07ff-4135-93f5-7e04be935520',
};

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('about');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getAboutPageData(aboutIds, onProgress),
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setAboutData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!aboutData?.textPages) return null;
    return aboutData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout>
      <HeroSection
        imageUrl={
          isDarkMode
            ? aboutData?.heroSection?.darkModeImageUrl ||
              aboutData?.heroSection?.lightModeImageUrl
            : aboutData?.heroSection?.lightModeImageUrl
        }
        title={aboutData?.heroSection?.title}
        titleHighlight={aboutData?.heroSection?.titleHighlight}
        subtitle={aboutData?.heroSection?.subtitle}
        description={aboutData?.heroSection?.description}
        buttonText={aboutData?.heroSection?.buttonText}
        buttonLink={aboutData?.heroSection?.buttonLink}
        showThemeToggle={aboutData?.heroSection?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && aboutData?.newsIcons?.items && (
        <NewsIcons items={aboutData.newsIcons.items} />
      )}
      {!isLoading && aboutData?.rollingMenu?.data && (
        <RollingMenu rollingMenu={aboutData.rollingMenu} />
      )}
      {!isLoading && getParagraphById(pID.P_2) && (
        <TextPage
          key={getParagraphById(pID.P_2)?._id}
          type={getParagraphById(pID.P_2)?.type}
          titleActive={getParagraphById(pID.P_2)?.titleActive}
          titleInactive={getParagraphById(pID.P_2)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_2)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_2)?.paragraphText}
          buttonText={getParagraphById(pID.P_2)?.buttonText}
          buttonLink={getParagraphById(pID.P_2)?.buttonLink}
        />
      )}
      {!isLoading && aboutData?.aboutTeamMembers?.length > 0 && (
        <div className='page-wrapper flex justify-between about-cards'>
          {aboutData.aboutTeamMembers?.map((card) => (
            <ImageCard
              key={card.titleItalic + card.titleGrotesk}
              {...card}
              imageUrl={
                isDarkMode
                  ? card.darkModeImageUrl || card.lightModeImageUrl
                  : card.lightModeImageUrl
              }
            />
          ))}
        </div>
      )}
      {!isLoading && aboutData?.thinImages?.data && (
        <ThinImagesSection
          cards={aboutData.thinImages.data}
          titleGrotesk={aboutData.thinImages.groteskTitle}
          titleItalic={aboutData.thinImages.italicTitle}
          grid={aboutData.thinImages.grid}
        />
      )}
      {!isLoading && getParagraphById(pID.P_3) && (
        <TextPage
          key={getParagraphById(pID.P_3)?._id}
          type={getParagraphById(pID.P_3)?.type}
          titleActive={getParagraphById(pID.P_3)?.titleActive}
          titleInactive={getParagraphById(pID.P_3)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_3)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_3)?.paragraphText}
          buttonText={getParagraphById(pID.P_3)?.buttonText}
          buttonLink={getParagraphById(pID.P_3)?.buttonLink}
        />
      )}
      {!isLoading && aboutData?.homeSlider?.slides && (
        <HomeFPCarousel panelData={aboutData.homeSlider} />
      )}
      {!isLoading && getParagraphById(pID.P_4) && (
        <TextPage
          key={getParagraphById(pID.P_4)?._id}
          type={getParagraphById(pID.P_4)?.type}
          titleActive={getParagraphById(pID.P_4)?.titleActive}
          titleInactive={getParagraphById(pID.P_4)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_4)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_4)?.paragraphText}
          buttonText={getParagraphById(pID.P_4)?.buttonText}
          buttonLink={getParagraphById(pID.P_4)?.buttonLink}
        />
      )}
      {!isLoading && <Brochure data={aboutData?.brochureData} />}
    </PageLayout>
  );
};

export default About;
